/**
 * Live, traffic-aware drive-time lookup via the Mapbox Matrix API — used only
 * on explicit request (the "Refine" button), never as the default coverage
 * engine (see docs/response-coverage-2.0.md for why: cost-avoidance, and the
 * modelled graph estimate needs to work with no incident-specific network
 * call). One matrix call covers the incident → up to a handful of stations,
 * so "refine the closest three" costs exactly one request, not three.
 */
import type { LngLat } from '@duty/shared';
import { MAPBOX_TOKEN } from '@/config/mapbox';

export interface LiveDuration {
  stationId: string;
  /** Minutes, traffic-aware, or `undefined` if Mapbox couldn't route to this destination. */
  minutes: number | undefined;
}

/**
 * Traffic-aware drive minutes from `origin` to each of `destinations`, via one
 * Mapbox Directions Matrix request. Returns `undefined` (never throws) if the
 * whole request fails (network, quota, bad token) — the caller should treat
 * that as "live estimate unavailable", not silently fall back to a fabricated
 * number.
 */
export async function fetchLiveDriveMinutes(
  origin: LngLat,
  destinations: { stationId: string; coord: LngLat }[],
): Promise<LiveDuration[] | undefined> {
  if (!MAPBOX_TOKEN || destinations.length === 0) return undefined;
  const coordsList = [origin, ...destinations.map((d) => d.coord)];
  const coords = coordsList.map((c) => `${c.lng},${c.lat}`).join(';');
  const url =
    `https://api.mapbox.com/directions-matrix/v1/mapbox/driving-traffic/${coords}` +
    `?sources=0&annotations=duration&access_token=${MAPBOX_TOKEN}`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) return undefined;
    const data = (await res.json()) as { durations?: (number | null)[][] };
    const row = data.durations?.[0];
    if (!row) return undefined;
    return destinations.map((d, i) => {
      const seconds = row[i + 1];
      return { stationId: d.stationId, minutes: seconds != null ? seconds / 60 : undefined };
    });
  } catch {
    return undefined;
  } finally {
    clearTimeout(timeout);
  }
}

export interface DirectionRoute {
  stationId: string;
  geometry: { type: 'LineString'; coordinates: [number, number][] };
}

/**
 * Fetch driving directions with geometry from origin to each destination.
 * Returns GeoJSON LineString geometries for drawing on map, keyed by stationId.
 */
export async function fetchDirections(
  origin: LngLat,
  destinations: { stationId: string; coord: LngLat }[],
): Promise<DirectionRoute[] | undefined> {
  if (!MAPBOX_TOKEN || destinations.length === 0) return undefined;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const routes: DirectionRoute[] = [];
    for (const dest of destinations) {
      const coords = `${origin.lng},${origin.lat};${dest.coord.lng},${dest.coord.lat}`;
      const url = `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${coords}?geometries=geojson&access_token=${MAPBOX_TOKEN}`;
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) continue;
      const data = (await res.json()) as { routes?: Array<{ geometry: { type: string; coordinates: [number, number][] } }> };
      const route = data.routes?.[0];
      if (route?.geometry?.coordinates) {
        routes.push({ stationId: dest.stationId, geometry: route.geometry as { type: 'LineString'; coordinates: [number, number][] } });
      }
    }
    return routes.length > 0 ? routes : undefined;
  } catch {
    return undefined;
  } finally {
    clearTimeout(timeout);
  }
}
